import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Grid,
  IconButton, InputAdornment, Paper, TextField, Tooltip, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import LanguageIcon from '@mui/icons-material/Language';
import LoginIcon from '@mui/icons-material/Login';
import BusinessIcon from '@mui/icons-material/Business';
import LockResetIcon from '@mui/icons-material/LockReset';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import { useTranslation } from 'react-i18next';
import { LANGUAGE_LABELS } from '../../utils/languages';
import { useUserStore } from '../../store';
import { numFont } from '../../utils/fonts';

function InfoItem({ label, value, icon, xs = 12, sm = 6 }) {
  return (
    <Grid item xs={xs} sm={sm}>
      <Paper variant="outlined" sx={{ p: 1.5, overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          {icon && <Box sx={{ color: 'text.disabled', flexShrink: 0 }}>{icon}</Box>}
          <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
            <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
            <Typography variant="body2" fontWeight={500} sx={{ overflow: 'hidden', textOverflow: 'ellipsis', wordBreak: 'break-word' }}>
              {value || '-'}
            </Typography>
          </Box>
        </Box>
      </Paper>
    </Grid>
  );
}

const ROLE_COLORS = { admin: 'error', foreman: 'warning', accountant: 'info' };

/** One Verstaani user: flags, memberships across clients, and the staff-side password reset.
 * The generated password is shown once, right after the reset, and never again. */
export default function UserDetailDialog({ open, onClose, userId, onChanged }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { user: currentUser } = useUserStore();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [resetError, setResetError] = useState(null);
  const [newPassword, setNewPassword] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open || !userId) return;
    setLoading(true);
    setError(null);
    setResetError(null);
    setNewPassword(null);
    setConfirmOpen(false);
    axios.get(`/api/vadmin/users/${userId}/`)
      .then((res) => setUser(res.data))
      .catch(() => setError(t('vadmin.usersTab.load_failed')))
      .finally(() => setLoading(false));
  }, [open, userId, t]);

  const formatDateTime = (value) => (value ? new Date(value).toLocaleString('fi-FI') : t('vadmin.never'));
  const formatDate = (value) => (value ? new Date(value).toLocaleDateString('fi-FI') : '-');

  const roleLabel = (role) => ({
    admin: t('vadmin.roleAdmin'),
    foreman: t('vadmin.roleForeman'),
    employee: t('vadmin.roleEmployee'),
    accountant: t('vadmin.roleAccountant'),
  }[role] || role);

  const isSuperuser = Boolean(currentUser?.is_superuser);
  const targetIsStaff = Boolean(user?.is_staff || user?.is_superuser);
  const canReset = Boolean(user?.is_active) && (isSuperuser || !targetIsStaff);

  const resetPassword = async () => {
    setBusy(true);
    setResetError(null);
    try {
      const { data } = await axios.post(`/api/vadmin/users/${userId}/reset-password/`);
      setConfirmOpen(false);
      setCopied(false);
      setNewPassword(data.password);
      if (onChanged) onChanged();
    } catch (err) {
      setResetError(err?.response?.data?.detail || t('vadmin.usersTab.reset_failed'));
    } finally {
      setBusy(false);
    }
  };

  const copy = () => {
    navigator.clipboard.writeText(newPassword);
    setCopied(true);
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" fullScreen={fullScreen} data-testid="vadmin-user-dialog">
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 6 }}>
          <PersonIcon color="action" />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" component="div" noWrap>{user?.name || user?.email || t('vadmin.usersTab.detail_title')}</Typography>
            {user?.name && <Typography variant="body2" color="text.secondary" noWrap>{user.email}</Typography>}
          </Box>
          <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }} aria-label={t('Close')}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>}
          {error && <Alert severity="error">{error}</Alert>}
          {!loading && !error && user && (
            <>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                <Chip size="small" color={user.is_active ? 'success' : 'error'} label={user.is_active ? t('vadmin.usersTab.active') : t('vadmin.usersTab.inactive')} />
                {user.is_superuser && <Chip size="small" color="warning" label={t('vadmin.usersTab.superuser')} />}
                {user.is_staff && !user.is_superuser && <Chip size="small" color="warning" variant="outlined" label={t('vadmin.usersTab.staff')} />}
                <Chip size="small" color={user.mfa_enabled ? 'success' : 'default'} variant="outlined" label={user.mfa_enabled ? t('vadmin.usersTab.mfa_on') : t('vadmin.usersTab.mfa_off')} />
                {isSuperuser && user.is_active && (
                  <Tooltip title={t('vadmin.impersonate')}>
                    <IconButton size="small" color="warning" onClick={() => { window.location.href = `/api/impersonate/${user.id}/`; }}>
                      <LoginIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>

              <Grid container spacing={1.5} sx={{ mb: 2 }}>
                <InfoItem label={t('vadmin.usersTab.email')} value={user.email} icon={<EmailIcon fontSize="small" />} />
                <InfoItem label={t('vadmin.usersTab.user_id')} value={<Box component="span" sx={numFont}>{user.id}</Box>} />
                <InfoItem label={t('vadmin.usersTab.last_login')} value={formatDateTime(user.last_login)} />
                <InfoItem label={t('vadmin.usersTab.joined')} value={formatDateTime(user.date_joined)} />
                <InfoItem
                  label={t('vadmin.usersTab.language')}
                  value={user.preferred_language ? (LANGUAGE_LABELS[user.preferred_language] ?? user.preferred_language) : t('vadmin.usersTab.language_default')}
                  icon={<LanguageIcon fontSize="small" />}
                />
              </Grid>

              <Typography variant="subtitle2" sx={{ mb: 1, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'text.secondary', fontSize: '12px' }}>
                {t('vadmin.usersTab.memberships')} ({user.memberships.length})
              </Typography>
              {user.memberships.length === 0 ? (
                <Typography variant="body2" color="text.secondary">{t('vadmin.usersTab.no_memberships')}</Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {user.memberships.map((m) => (
                    <Paper key={m.organization_id} variant="outlined" sx={{ p: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                        <BusinessIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={600} noWrap>{m.organization_name}</Typography>
                          <Typography variant="caption" color="text.secondary" noWrap display="block">{m.business_id || ''}</Typography>
                        </Box>
                        <Chip size="small" variant="outlined" color={ROLE_COLORS[m.role] || 'default'} label={roleLabel(m.role)} sx={{ fontSize: '11px', height: 22 }} />
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="caption" color="text.disabled" display="block">{t('vadmin.usersTab.member_since')}</Typography>
                          <Typography variant="caption" fontWeight={500} sx={numFont}>{formatDate(m.created_at)}</Typography>
                        </Box>
                      </Box>
                    </Paper>
                  ))}
                </Box>
              )}

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" sx={{ mb: 1, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'text.secondary', fontSize: '12px' }}>
                {t('vadmin.usersTab.reset_password')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                {t('vadmin.usersTab.reset_password_intro')}
              </Typography>
              {!user.is_active && <Alert severity="info" sx={{ mb: 1.5 }}>{t('vadmin.usersTab.reset_password_inactive')}</Alert>}
              {user.is_active && targetIsStaff && !isSuperuser && (
                <Alert severity="info" sx={{ mb: 1.5 }}>{t('vadmin.usersTab.reset_password_staff_only')}</Alert>
              )}
              {resetError && <Alert severity="error" sx={{ mb: 1.5 }}>{resetError}</Alert>}
              <Button
                variant="outlined"
                color="warning"
                startIcon={<LockResetIcon />}
                disabled={!canReset || busy}
                onClick={() => setConfirmOpen(true)}
                data-testid="vadmin-user-reset-password"
              >
                {t('vadmin.usersTab.reset_password_button')}
              </Button>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose} variant="outlined">{t('Close')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmOpen} onClose={() => !busy && setConfirmOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>{t('vadmin.usersTab.reset_confirm_title')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">{t('vadmin.usersTab.reset_confirm_body', { email: user?.email || '' })}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={busy}>{t('Cancel')}</Button>
          <Button color="warning" variant="contained" onClick={resetPassword} disabled={busy} data-testid="vadmin-user-reset-confirm">
            {t('vadmin.usersTab.reset_confirm_button')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(newPassword)} fullWidth maxWidth="sm" fullScreen={fullScreen}>
        <DialogTitle>{t('vadmin.usersTab.new_password_title')}</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>{t('vadmin.usersTab.new_password_warning')}</Alert>
          <Typography variant="body2" sx={{ mb: 1 }}>{t('vadmin.usersTab.new_password_for', { email: user?.email || '' })}</Typography>
          <TextField
            fullWidth
            size="small"
            value={newPassword || ''}
            InputProps={{
              readOnly: true,
              sx: { fontFamily: 'monospace', fontSize: '16px', letterSpacing: '0.08em' },
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title={copied ? t('vadmin.usersTab.copied') : t('vadmin.usersTab.copy')}>
                    <IconButton size="small" onClick={copy}>
                      {copied ? <CheckIcon color="success" /> : <ContentCopyIcon fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
            inputProps={{ 'data-testid': 'vadmin-user-new-password' }}
          />
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setNewPassword(null)} data-testid="vadmin-user-password-saved-it">
            {t('vadmin.usersTab.saved_it')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
